import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import SeoRelatedLinks from "../../../components/seo/SeoRelatedLinks.jsx";
import SeoHeader from "../../../components/seo/SeoHeader.jsx";
import SeoContactSection from "../../../components/seo/SeoContactSection.jsx";
import SeoWorkSteps from "../../../components/seo/SeoWorkSteps.jsx";

const sections = [
  {
    id: "start",
    label:
      "Bilder i en meny bør hjelpe gjesten, ikke bare dekorere siden",
  },
  {
    id: "why",
    label: "Hvorfor kan bilder være viktige i en digital meny?",
  },
  {
    id: "where",
    label: "Hvilke retter bør få bilder først?",
  },
  {
    id: "quality",
    label: "Hvilken bildekvalitet trenger en god meny?",
  },
  {
    id: "workflow",
    label: "Hvordan administrerer du bilder i en digital meny?",
  },
  {
    id: "mistakes",
    label: "Hvilke feil skjer med menyer som bruker bilder?",
  },
  {
    id: "recommendation",
    label: "Hvordan ville jeg startet som restauranteier?",
  },
  {
    id: "next-steps",
    label: "Hva bør du gjøre nå?",
  },
  {
    id: "bestplace",
    label: "Hvordan Best Place hjelper deg med å lage en meny med bilder",
  },
  {
    id: "faq",
    label: "FAQ om menyer med bilder",
  },
];

const CALLBACK_API_ORIGIN =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1")
    ? "http://localhost:3030"
    : "";

function VisualIdea({ children }) {
  return (
    <div className="seoVisualIdea" aria-label="Visuell idé">
      <div className="seoVisualIdea__icon" aria-hidden="true">
        ✦
      </div>
      <div>
        <strong>Idé til illustrasjon:</strong>
        <p>{children}</p>
      </div>
    </div>
  );
}

export default function MenuWithPicturesNo() {
  useEffect(() => {
    document.documentElement.setAttribute("lang", "no");
    document.title =
      "Meny med bilder: presenter restaurantretter bedre online | Best Place";
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <main className="qrArticle">
      <SeoHeader
        lang="no"
        langLinks={{
          de: "/de/speisekarte-mit-bildern",
          en: "/en/menu-with-pictures",
          no: "/no/meny-med-bilder",
        }}
      />

      <section className="qrArticle__hero">
        <div className="qrArticle__heroInner">
          <h1 className="qrArticle__heroTitle">
            <span>
              Meny med bilder: presenter restaurantretter bedre online
            </span>
          </h1>

          <div className="qrArticle__topsection">
            <div className="qrArticle__topsection--img-wrapper">
              <div className="qrArticle__topsection--img"></div>
            </div>

            <div className="qrArticle__topsection--text-wrapper">
              <p className="seoHero__lead">
                Bilder kan gjøre en restaurantmeny sterkere. Et godt bilde
                hjelper gjesten med å forstå en rett raskere, gjør anbefalinger
                mer synlige og kan være spesielt nyttig for nye, sesongbaserte
                eller ukjente retter.
              </p>

              <p className="seoHero__text">
                Men bilder er ikke automatisk nyttige. Dårlige bilder,
                ujevn stil eller for mange bilder kan gjøre en digital meny
                tung og uoversiktlig. Det viktige spørsmålet er hvor bildene
                faktisk hjelper gjesten med å ta en beslutning.
              </p>
            </div>
          </div>
        </div>
      </section>

      <SeoContactSection lang="no" apiOrigin={CALLBACK_API_ORIGIN} />

      <section className="articls">
        <h2>Innhold i artikkelen</h2>

        <nav className="qrArticle__nav" aria-label="Artikkelnavigasjon">
          <ol className="qrArticle__navList">
            {sections.map((item, index) => (
              <li key={item.id} className="qrArticle__navItem">
                <button
                  type="button"
                  className="qrArticle__navButton"
                  onClick={() => scrollToSection(item.id)}
                >
                  <span className="qrArticle__navNumber">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <span className="qrArticle__navLabel">{item.label}</span>
                </button>
              </li>
            ))}
          </ol>
        </nav>
      </section>

      <article>
        <section id="start" className="acquaintance">
          <h2>
            Bilder i en meny bør hjelpe gjesten, ikke bare dekorere siden
          </h2>

          <p>
            Mange restauranter starter med tanken om at hver rett trenger et
            bilde. Det høres logisk ut, men det er ikke alltid den beste
            løsningen. På en lang meny kan for mange bilder virke støyende og
            gjøre mobilopplevelsen tregere.
          </p>

          <p>
            En bedre tilnærming er selektiv: bruk bilder der de hjelper gjesten
            med å velge. For eksempel til anbefalinger, nye retter,
            signaturretter, desserter, cocktails eller sesongtilbud.
          </p>

          <div className="seoComparison">
            <div className="seoComparison__col">
              <h3>Svakere</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">
                  Hver rett får et tilfeldig tilgjengelig bilde.
                </li>
                <li className="text__seo-li">Ulik belysning og stil.</li>
                <li className="text__seo-li">Mørke eller uklare bilder.</li>
                <li className="text__seo-li">
                  Menyen blir treg og overfylt.
                </li>
                <li className="text__seo-li">
                  Bildene passer ikke til den ekte retten.
                </li>
              </ul>
            </div>

            <div className="seoComparison__col seoComparison__col--highlight">
              <h3>Bedre</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">
                  Bruk bilder for viktige retter først.
                </li>
                <li className="text__seo-li">Hold én visuell stil.</li>
                <li className="text__seo-li">Bruk lyse og tydelige bilder.</li>
                <li className="text__seo-li">
                  Ta hensyn til lastehastighet på mobil.
                </li>
                <li className="text__seo-li">
                  Vis realistisk hva gjesten får.
                </li>
              </ul>
            </div>
          </div>

          <h3>Test bilder med noen få retter først</h3>

          <p>
            Start med anbefalinger, dagens tilbud eller bestselgere. Da er det
            lettere å se om bilder forbedrer den digitale menyen uten å gjøre
            siden for tung.
          </p>
        </section>

        <section id="why" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Hvorfor kan bilder være viktige i en digital meny?</h2>

            <p>
              Gjester bestemmer seg ikke bare ved å lese. Navn, priser og
              beskrivelser betyr mye, men bilder kan kommunisere raskere:
              porsjonsstørrelse, stil, ingredienser, presentasjon og stemning.
            </p>

            <p>
              Bilder er spesielt nyttige når en rett er vanskelig å forestille
              seg, når gjester ikke kjenner kjøkkenet, eller når restauranten
              vil fremheve bestemte produkter.
            </p>

            <h3>Bilder kan gjøre beslutningen enklere</h3>

            <p>
              En meny med bilder er ikke bare et designvalg. Den kan påvirke hvor
              raskt gjester forstår retter og hvilke tilbud de legger merke til
              først.
            </p>

            <p>
              De sterkeste bildene dekorerer ikke bare menyen. De svarer på et
              ekte spørsmål fra gjesten: hvordan ser retten ut, hvordan serveres
              den, og passer den til det jeg ønsker akkurat nå?
            </p>
          </div>
        </section>

        <section id="where" className="acquaintance">
          <h2>Hvilke retter bør få bilder først?</h2>

          <p>
            Ikke alle retter trenger et bilde. I mange tilfeller er mindre
            bedre. De beste bildene er de som løser et konkret spørsmål for
            gjesten.
          </p>

          <ul className="qrArticle__block-li">
            <li className="text__seo-li">Husets anbefalinger.</li>
            <li className="text__seo-li">Dagens tilbud.</li>
            <li className="text__seo-li">Sesongtilbud.</li>
            <li className="text__seo-li">Desserter.</li>
            <li className="text__seo-li">Cocktails og drikke.</li>
            <li className="text__seo-li">Ukjente spesialiteter.</li>
            <li className="text__seo-li">Retter for turister.</li>
            <li className="text__seo-li">Retter i høyere prisklasse.</li>
          </ul>

          <p>
            Et bilde er spesielt nyttig når det svarer på spørsmål: Hvordan ser
            retten ut? Hvor stor er porsjonen? Er den lett, kraftig, moderne
            eller tradisjonell?
          </p>

          <VisualIdea>
            En digital meny med tre fremhevede produkter: dagens anbefaling,
            dessert og cocktail. Alle bildene har samme lyse stil.
          </VisualIdea>
        </section>

        <section id="quality" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Hvilken bildekvalitet trenger en god meny?</h2>

            <p>
              Gode menybilder trenger ikke se ut som en stor reklamekampanje.
              Men de bør være rene, lyse, realistiske og konsekvente.
            </p>

            <p>
              Gjesten bør kunne forvente noe som ligner på det han eller hun ser
              på bildet. Et bilde som ser mye bedre ut enn den ekte retten kan
              skape oppmerksomhet på kort sikt, men det kan skade tilliten senere.
            </p>

            <ul className="qrArticle__block-li">
              <li className="text__seo-li">Naturlig lys.</li>
              <li className="text__seo-li">Rolig bakgrunn.</li>
              <li className="text__seo-li">Ekte rett.</li>
              <li className="text__seo-li">Konsekvent bildestil.</li>
              <li className="text__seo-li">Ikke for mange filtre.</li>
              <li className="text__seo-li">Tydelig skarphet.</li>
              <li className="text__seo-li">Filstørrelse tilpasset mobil.</li>
              <li className="text__seo-li">Realistisk porsjon.</li>
            </ul>

            <h3>Bilder er en del av restaurantens digitale presentasjon</h3>

            <p>
              I en digital meny blir bildene en del av hvordan restauranten
              oppfattes. De bør bygge tillit, ikke skape urealistiske
              forventninger.
            </p>
          </div>
        </section>

        <section id="workflow" className="acquaintance">
          <h2>Hvordan administrerer du bilder i en digital meny?</h2>

          <p>
            Den beste arbeidsflyten er enkel: ikke start med 80 bilder. Start
            med et lite utvalg og sjekk om bildene faktisk hjelper inne i menyen.
          </p>

          <ol className="seoSteps">
            <li className="text__seo-li">
              <strong>Velg retter:</strong> Start med bestselgere,
              anbefalinger eller sesongtilbud.
            </li>

            <li className="text__seo-li">
              <strong>Velg én visuell stil:</strong> Lignende vinkel, lignende
              lys og en rolig bakgrunn.
            </li>

            <li className="text__seo-li">
              <strong>Last opp bilder:</strong> Bruk bilder bare der de hjelper
              gjesten.
            </li>

            <li className="text__seo-li">
              <strong>Sjekk på smarttelefon:</strong> Pass på at menyen laster
              raskt og fortsatt er lett å lese.
            </li>

            <li className="text__seo-li">
              <strong>Bytt ut jevnlig:</strong> Sesongretter og dagens tilbud
              trenger aktuelle bilder.
            </li>
          </ol>

          <p>
            En digital meny er mer fleksibel enn en trykt meny. Bilder kan
            legges til, byttes eller fjernes uten å trykke QR-koden på nytt.
          </p>
        </section>

        <section id="mistakes" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Hvilke feil skjer med menyer som bruker bilder?</h2>

            <p>
              Bilder kan hjelpe, men de kan også skade. Det skjer vanligvis når
              de legges inn uten en tydelig hensikt.
            </p>

            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>Vanlige feil</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">For mange bilder samtidig.</li>
                  <li className="text__seo-li">Mørke eller uklare bilder.</li>
                  <li className="text__seo-li">Ulik bildestil.</li>
                  <li className="text__seo-li">
                    Overredigerte bilder som virker urealistiske.
                  </li>
                  <li className="text__seo-li">
                    Store filer som gjør menyen treg.
                  </li>
                  <li className="text__seo-li">
                    Bildene passer ikke til den ekte retten.
                  </li>
                  <li className="text__seo-li">
                    Gamle bilder blir liggende etter at retten er endret.
                  </li>
                </ul>
              </div>

              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>Bedre</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Start med noen få viktige bilder.
                  </li>
                  <li className="text__seo-li">
                    Bruk lyse og tydelige bilder.
                  </li>
                  <li className="text__seo-li">Hold en konsekvent stil.</li>
                  <li className="text__seo-li">
                    Vis rettene realistisk.
                  </li>
                  <li className="text__seo-li">
                    Optimaliser bilder for mobilbruk.
                  </li>
                  <li className="text__seo-li">Gå gjennom bilder jevnlig.</li>
                  <li className="text__seo-li">
                    Fjern gamle bilder etter menyendringer.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section id="recommendation" className="acquaintance">
          <h2>Hvordan ville jeg startet som restauranteier?</h2>

          <p>
            Jeg ville ikke prøvd å fotografere hver eneste rett med en gang. Det
            tar for mye tid og gir ofte ujevne resultater.
          </p>

          <ol className="seoSteps">
            <li className="text__seo-li">
              <strong>Start med 5 til 10 retter:</strong> Anbefalinger,
              bestselgere eller sesongretter.
            </li>

            <li className="text__seo-li">
              <strong>Velg én bildestil:</strong> Samme sted, lignende lys og
              lignende vinkel.
            </li>

            <li className="text__seo-li">
              <strong>Test på smarttelefon:</strong> Sjekk om bildene ser bra
              ut og om menyen ikke føles overfylt.
            </li>

            <li className="text__seo-li">
              <strong>Observer i én uke:</strong> Spør gjestene om disse
              rettene? Blir de lettere å forklare?
            </li>

            <li className="text__seo-li">
              <strong>Utvid senere:</strong> Legg til flere bilder bare der de
              virkelig hjelper.
            </li>
          </ol>

          <p>
            Målet er ikke å bygge et fotoarkiv. Målet er å gjøre gjestens valg
            enklere.
          </p>
        </section>

        <section id="next-steps" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Hva bør du gjøre nå?</h2>

            <p>
              Hvis du vil lage en meny med bilder, start med en liten og ryddig
              test.
            </p>

            <ul className="qrArticle__block-li">
              <li className="text__seo-li">Opprett en digital meny.</li>
              <li className="text__seo-li">
                Velg 5 til 10 viktige retter.
              </li>
              <li className="text__seo-li">
                Ta lyse og realistiske bilder.
              </li>
              <li className="text__seo-li">
                Last opp bildene til menyen.
              </li>
              <li className="text__seo-li">
                Åpne menyen på en smarttelefon.
              </li>
              <li className="text__seo-li">
                Sjekk lastehastighet og lesbarhet.
              </li>
              <li className="text__seo-li">
                Legg til flere bilder først etter den første testen.
              </li>
            </ul>

            <VisualIdea>
              En liten bildetest: fem utvalgte retter, én konsekvent bildestil
              og en smarttelefonvisning som viser om menyen fortsatt er tydelig
              og rask.
            </VisualIdea>
          </div>
        </section>

        <SeoWorkSteps lang="no" />

        <section id="faq" className="acquaintance">
          <h2>FAQ om menyer med bilder</h2>

          <div className="seoFaq">
            <details>
              <summary>Bør hver rett ha et bilde?</summary>
              <p>
                Ikke nødvendigvis. Det er ofte bedre å bruke bilder selektivt
                for anbefalinger, bestselgere, dagens tilbud eller retter som er
                vanskelige å forestille seg.
              </p>
            </details>

            <details>
              <summary>Kan jeg ta menybilder med en smarttelefon?</summary>
              <p>
                Ja, hvis lys, skarphet og stil er gode. Bildet bør vise
                realistisk hva gjesten kan forvente.
              </p>
            </details>

            <details>
              <summary>Kan bilder gjøre menyen tregere?</summary>
              <p>
                Store og uoptimaliserte bilder kan gjøre en digital meny tregere.
                Bilder bør velges nøye og optimaliseres for mobilbruk.
              </p>
            </details>

            <details>
              <summary>Er bilder bedre enn beskrivelser?</summary>
              <p>
                Nei. Gode bilder og korte beskrivelser fungerer sammen. Bildet
                skaper førsteinntrykket, mens beskrivelsen forklarer ingredienser,
                stil eller detaljer.
              </p>
            </details>

            <details>
              <summary>Hvilke retter bør jeg fotografere først?</summary>
              <p>
                Start med bestselgere, husets anbefalinger, sesongretter,
                desserter eller retter som gjestene kanskje ikke kjenner godt.
              </p>
            </details>

            <details>
              <summary>Kan jeg bytte bilder senere?</summary>
              <p>
                Ja. I en digital meny kan bilder byttes, fjernes eller legges
                til uten å trykke QR-koden på nytt.
              </p>
            </details>
          </div>
        </section>

        <SeoRelatedLinks
            lang="no"
            group="menu"
            exclude="/no/meny-med-bilder"
        />
      </article>
    </main>
  );
}